import { useState } from 'react';
import {
    PREMIUM_BOOST_PRODUCTS,
    EMERGENCY_OZONE_PRICE,
    PREMIUM_PLANET_IMAGE_PRICE,
    QUEUE_LIMITS,
    RESOURCE_NAMES,
} from '@kaeliux/shared';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { useAuthStore } from '../store/authStore';
import GuestLockedPage from '../components/auth/GuestLockedPage';
import { purchasePremiumBoost } from '../services/premium.service';
import { formatFullNumber } from '../utils/formatters';
import { Panel } from '../components/ui/Panel';
import { Chip } from '../components/ui/Chip';
import { Badge } from '../components/ui/Badge';
import { Icon } from '../components/ui/Icon';
import { Button } from '../components/ui/Button';

const tierLabel: Record<string, string> = {
    normal: 'Normal',
    premium: 'Premium',
    platinum: 'Platinum',
};

export default function PremiumPage() {
    const isGuest = useAuthStore((s) => s.session?.isGuest ?? false);
    if (isGuest) {
        return (
            <GuestLockedPage title="Premium Store">
                The Premium Store opens once you create an account. You can still spend your Dark Matter on rushes and
                boosts around your empire.
            </GuestLockedPage>
        );
    }
    return <PremiumStore />;
}

function PremiumStore() {
    const premium = usePlayerStore((s) => s.premium);
    const effectiveTier = usePlayerStore(selectEffectiveTier);
    const darkMatter = premium?.darkMatter ?? 0;
    const tierGrant = premium?.tier;
    const tierActive = tierGrant !== undefined && tierGrant.expiresAt > Date.now();

    const [busyProduct, setBusyProduct] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function buy(productId: string) {
        setBusyProduct(productId);
        setError(null);
        try {
            const { premium: next } = await purchasePremiumBoost(productId);
            usePlayerStore.setState({ premium: next });
        } catch (e) {
            setError((e as Error)?.message ?? 'Purchase failed');
        } finally {
            setBusyProduct(null);
        }
    }

    const products = Object.values(PREMIUM_BOOST_PRODUCTS);

    return (
        <main className="max-w-4xl mx-auto px-4 py-6 flex flex-col gap-6">
            <header>
                <Panel variant="solid" padding="lg" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-display text-content-primary flex items-center gap-2">
                            <Icon name="dark-matter" size={20} className="text-status-warning" /> Premium Store
                        </h1>
                        <p className="text-content-muted text-body mt-1">
                            Spend Dark Matter on tier boosts, Emergency {RESOURCE_NAMES.ozone}, and planet skins.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-content-muted text-micro uppercase tracking-wide">Dark Matter</div>
                        <div className="text-display text-status-warning" data-testid="dark-matter-balance">
                            {formatFullNumber(darkMatter)}
                        </div>
                        <div className="text-content-muted text-micro mt-1">
                            Tier: <Badge tone="info" data-testid="effective-tier">{tierLabel[effectiveTier]}</Badge>
                        </div>
                    </div>
                </Panel>
            </header>

            {tierActive && (
                <Chip tone="success" data-testid="active-boost" className="self-start">
                    Active tier: {tierLabel[tierGrant!.tier]} until {new Date(tierGrant!.expiresAt).toLocaleString()}
                </Chip>
            )}

            {error && <p className="text-status-danger text-body" role="alert">{error}</p>}

            <section className="flex flex-col gap-3">
                <h2 className="text-heading text-content-primary">Premium Boosts</h2>
                <p className="text-content-muted text-body">
                    A time-limited tier upgrade — bigger build/research/ship-upgrade queues, more saved battle
                    reports, and visibility of nearby {RESOURCE_NAMES.scrap} for its duration.
                </p>
                <p className="text-content-muted text-body" data-testid="tier-queue-perks">
                    Building queue: {QUEUE_LIMITS.normal} slots, {QUEUE_LIMITS.premium} with Premium,{' '}
                    {QUEUE_LIMITS.platinum} with Platinum. Platinum also adds Queue max: one click queues every
                    level of a building that fits.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                    {products.map((product) => {
                        const affordable = darkMatter >= product.price;
                        return (
                            <Panel
                                key={product.id}
                                variant="solid"
                                className="flex items-center justify-between gap-3"
                                data-testid={`boost-product-${product.id}`}
                            >
                                <div>
                                    <div className="font-semibold text-content-primary">{product.label}</div>
                                    <div className="text-content-muted text-body">{formatFullNumber(product.price)} Dark Matter</div>
                                </div>
                                <Button
                                    type="button"
                                    variant="accent"
                                    size="md"
                                    onClick={() => buy(product.id)}
                                    loading={busyProduct === product.id}
                                    disabled={busyProduct !== null || !affordable}
                                >
                                    {busyProduct === product.id ? 'Buying…' : affordable ? 'Buy' : 'Not enough'}
                                </Button>
                            </Panel>
                        );
                    })}
                </div>
            </section>

            <section>
                <Panel variant="solid" className="text-body text-content-secondary flex flex-col gap-1">
                    <h2 className="text-title text-content-primary">Also spendable elsewhere</h2>
                    <p><Icon name="capsule" size={16} className="inline align-[-2px]" /> <span className="font-semibold">{`Emergency ${RESOURCE_NAMES.ozone}`}</span> — {EMERGENCY_OZONE_PRICE} Dark Matter, from the rescue screen of a planet in Drought.</p>
                    <p><Icon name="planet" size={16} className="inline align-[-2px]" /> <span className="font-semibold">Premium planet skins</span> — {PREMIUM_PLANET_IMAGE_PRICE} Dark Matter each, from the &quot;Change Photo&quot; picker on any planet.</p>
                </Panel>
            </section>

            <section>
                <Panel variant="solid" className="border-dashed flex items-center justify-between gap-3">
                    <div>
                        <div className="font-semibold text-content-secondary">Get more Dark Matter</div>
                        <div className="text-content-muted text-body">Buying Dark Matter with real money is coming soon.</div>
                    </div>
                    <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        disabled
                        data-testid="buy-dark-matter"
                    >
                        Coming soon
                    </Button>
                </Panel>
            </section>
        </main>
    );
}
